<template>
  <div class="live">
    <transition name="fade">
      <div v-if="competitionData" v-show="wsConnected">
        <LiveTableSolo
          v-if="routeStage"
          :current-stage="currentStage"
          :live-results="liveResults"
          :result-precision="competition.precision"
        />
        <LiveTableMulti
          v-else
          :hero-title="heroTitle"
          :live-results="liveResults"
          :result-precision="competition.precision"
          :now-stage="recentMessage.stageNo"
          :live-stages="liveStages"
          :pilot="competition.pilot"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import LiveTableSolo from "@/components/LiveTableSolo"
import LiveTableMulti from "@/components/LiveTableMulti"
import { config } from "@/config"

export default {
  name: "Competition",

  props: {
    modal: [String, Boolean],
  },

  components: { LiveTableMulti, LiveTableSolo },

  data: () => ({
    wsConnected: false,
    isUpdating: false,
    competitionData: null,
    currentStage: null,
    currentHash: null,
    ws: null,
    afterStage: false,
  }),

  computed: {
    recentMessage: function () {
      if (this.competitionData.message.length === 0) return ""
      return this.competitionData.message.reduce((prev, current) =>
        prev.messageId > current.messageId ? prev : current
      )
    },
    competition: function () {
      return this.competitionData.competition[0]
    },
    liveResults: function () {
      let results = []

      this.competitionData.competitor.forEach((competitor) => {
        let result = this.getCompetitorTime(competitor.id, this.currentStage)
        console.log(result)
        const penalty = this.competition.penalties ? this.getCompetitorPenalty(competitor.id, this.currentStage) : 0
        const allTimes = []
        this.liveStages.forEach((stage) => {
          const timeObject = {}
          timeObject.result = this.getCompetitorTime(competitor.id, stage)
          timeObject.penalty = this.competition.penalties ? this.getCompetitorPenalty(competitor.id, stage) : 0
          timeObject.stageNo = stage.stageNo
          allTimes.push(timeObject)
          if (isNaN(timeObject.result) && !this.routeStage) {
            result = "NC"
          }
        })
        if (result) {
          results.push({ ...competitor, result, penalty, allTimes })
        }
      })

      results.sort((a, b) => {
        if ([0, 1].includes(this.currentStage.type)) {
          if (isNaN(a.result) && !isNaN(b.result)) return 1
          if (!isNaN(a.result) && isNaN(b.result)) return -1
          return this.competition.penalties ? a.result + a.penalty - (b.result + b.penalty) : a.result - b.result
        } else if ([2, 3].includes(this.currentStage.type)) {
          if (isNaN(a.result.time) && !isNaN(b.result.time)) return 1
          if (!isNaN(a.result.time) && isNaN(b.result.time)) return -1
          return a.result.time - b.result.time
        } else if ([4, 5].includes(this.currentStage.type)) {
          if (isNaN(a.result.points) && !isNaN(b.result.points)) return 1
          if (!isNaN(a.result.points) && isNaN(b.result.points)) return -1
          return a.result.points - b.result.points
        }
      })
      results.forEach((result, index, results) => {
        result.pos = index + 1
        if (this.currentStage.type < 2) {
          if (isNaN(result.result)) result.pos = "NC"
          else if (index !== 0) {
            let prevResult = results[index - 1].result + results[index - 1].penalty
            let thisResult = result.result + result.penalty
            if (prevResult === thisResult) {
              result.pos = "="
            }
          }
        } else {
          if (isNaN(result.result.time)) result.pos = "NC"
          else if (index !== 0) {
            let prevResult = results[index - 1].result.time
            let thisResult = result.result.time
            if (prevResult === thisResult) {
              results[index - 1].pos = "="
              result.pos = "="
            }
          }
        }
      })
      return results
    },
    liveStages: function () {
      const stageNos = [...new Set(this.competitionData.stage.map((stage) => stage.stageNo))].filter(
        (stageNo) => stageNo !== null
      )
      const stages = []
      stageNos.forEach((stageNo) => {
        const stagesWithTheSameNo = this.competitionData.stage.filter((stage) => stage.stageNo === stageNo)
        stagesWithTheSameNo.sort((a, b) => {
          const aTheSmallestChampionshipId = a.championshipIds
            .split(",")
            .reduce((acc, championshipId) => Math.min(acc, championshipId), 999999999)
          const bTheSmallestChampionshipId = b.championshipIds
            .split(",")
            .reduce((acc, championshipId) => Math.min(acc, championshipId), 999999999)
          return bTheSmallestChampionshipId - aTheSmallestChampionshipId
        })
        stages.push(stagesWithTheSameNo[0])
      })
      return stages
    },
    routeStage: function () {
      return this.$route.params.stage ? parseInt(this.$route.params.stage) : null
    },

    heroTitle: function () {
      return this.competition.dayNo
        ? `${this.competition.name} – ${this.$i18n.t("day")} ${this.competition.dayNo}`
        : this.competition.name
    },

    heroSubtitle: function () {
      const dateParts = this.competition.competitionDate.split("-")
      return (
        this.competition.cityName +
        ", " +
        this.$i18n.d(new Date(dateParts[0], dateParts[1] - 1, dateParts[2].substr(0, 2)), "long")
      )
    },
  },

  watch: {
    competitionData: {
      deep: true,
      handler() {},
    },
  },

  created() {
    const competitionName = this.$route.params.competition

    fetch(`${config.ManagerServer.rest.url}/globalView/${competitionName}`)
      .then((response) => {
        if (response.ok) return response.json()
        throw new Error("Competition not found")
      })
      .then((data) => {
        const dataCompetitionId = data.competitionId
        fetch(`${config.ManagerServer.rest.url}/websocket/${data.competitionId}`)
          .then((response) => response.json())
          .then((data) => {
            if (data.type !== "ok") return

            this.ws = new WebSocket(`wss://wyniki-online.pl:${data.port}`)

            this.ws.onmessage = (message) => {
              const msg = JSON.parse(message.data)

              if (msg.answer === "ok") {
                this.isUpdating = false
              } else if (msg.answer === "refresh") {
                window.location.reload()
              } else if (msg.type === "init" || msg.type === "archival") {
                this.currentHash = msg.hash
                this.competitionData = { ...msg.data }
                this.currentStage = this.competitionData.stage.find((stage) => stage.stageNo === this.routeStage)
              } else if (this.currentHash === msg.oldHash) {
                const table = this.getTableName(msg)

                this.currentHash = msg.hash

                switch (msg.type) {
                  case "insert":
                    table.push(msg.data)
                    break
                  case "update":
                    table.splice(this.getTableElementIndex(msg), 1, msg.data)
                    break
                  case "delete":
                    table.splice(this.getTableElementIndex(msg), 1)
                    break
                }

                if (this.isUpdating) {
                  this.ws.send(
                    JSON.stringify({
                      type: "reanswer",
                      hash: this.currentHash,
                    })
                  )
                }
              } else {
                this.isUpdating = true

                this.ws.send(JSON.stringify({ type: "reanswer", hash: this.currentHash }))
              }
            }

            this.ws.onopen = () => {
              this.ws.send(`{ "type": "init", "id": "${dataCompetitionId}" }`)
              this.wsConnected = true
              this.$emit("ws-change", true)
            }

            this.ws.onerror = () => {
              this.wsConnected = false
              this.$emit("ws-change", false)
            }

            this.ws.onclose = () => {
              this.wsConnected = false
              this.$emit("ws-change", false)
            }
          })
      })
  },

  beforeDestroy() {
    if (this.ws) this.ws.close()
    this.$emit("ws-change", false)
    this.$emit("show-penalties", false)
  },
  mounted() {},
  methods: {
    getTableName(msg) {
      return this.competitionData[msg.tableName]
    },

    getTableElementIndex(msg) {
      if (msg.tableName === "time") {
        return this.getTableName(msg).findIndex(
          (el) => el.competitorId === msg.data.competitorId && el.stageNo === msg.data.stageNo
        )
      }

      return this.getTableName(msg).findIndex((el) => el.id === msg.data.id)
    },

    getCompetitorTime: function (competitorId, stage) {
      if (stage === null) return null

      switch (stage.type) {
        case 0:
          if (this.afterStage) return this.getCompetitorTimeAfterStage(competitorId, stage)
          return this.getCompetitorTimeSingleStage(competitorId, stage)
        case 1:
          return this.getCompetitorTimeGroupedStages(competitorId, stage)
        case 2:
          return this.getCompetitorMinTimeSingleStage(competitorId, stage)
        case 3:
          return this.getCompetitorMinTimeGroupedStages(competitorId, stage)
      }
    },

    getCompetitorTimeSingleStage: function (competitorId, stage) {
      console.log(competitorId, stage)
      if (stage.stageNo) {
        const competitorTime = this.competitionData.time.find(
          (time) => time.stageNo === stage.stageNo && time.competitorId === competitorId
        )
        if (competitorTime.atypicalResultId) {
          const atypicalResult = this.competitionData.atypicalResult.find(
            (result) => result.atypicalResultId === competitorTime.atypicalResultId
          )

          return atypicalResult?.shortName
        }
        console.log(competitorTime.time)
        return competitorTime.time
      }

      return null
    },
    getCompetitorTimeAfterStage: function (competitorId, stage) {
      if (stage.stageNo) {
        const stageNoList = this.getGroupedStages(stage.group, 0).map((groupedStage) => groupedStage.stageNo)
        const competitorTimes = this.competitionData.time
          .filter(
            (time) =>
              stageNoList.includes(time.stageNo) && time.stageNo <= stage.stageNo && time.competitorId === competitorId
          )
          .sort((a, b) => b.stageNo - a.stageNo)
        let time = 0
        for (let competitorTime of competitorTimes) {
          if (competitorTime.atypicalResultId) {
            const atypicalResult = this.competitionData.atypicalResult.find(
              (result) => result.atypicalResultId === competitorTime.atypicalResultId
            )

            return atypicalResult?.shortName
          }
          if (competitorTime.time === null) return null
          time += competitorTime.time
        }
        return time
      }

      return null
    },

    getCompetitorMinTimeSingleStage: function (competitorId, stage) {
      const targetTime = stage.data
      if (stage.stageNo) {
        const competitorTime = this.competitionData.time.find(
          (time) => time.stageNo === stage.stageNo && time.competitorId === competitorId
        )
        if (competitorTime.atypicalResultId) {
          const atypicalResult = this.competitionData.atypicalResult.find(
            (result) => result.atypicalResultId === competitorTime.atypicalResultId
          )

          return atypicalResult?.shortName
        }
        if (competitorTime.time === null) return null
        return { time: Math.abs(targetTime - competitorTime.time), realTime: competitorTime.time }
      }

      return null
    },

    getGroupedStages: function (group, type) {
      let stageNos = new Set(
        this.competitionData.stage
          .filter((stage) => stage.group === group && stage.type === type)
          .map((stage) => stage.stageNo)
      )
      return [...stageNos].map((stageNo) => this.competitionData.stage.find((stage) => stage.stageNo === stageNo))
    },

    getCompetitorTimeGroupedStages: function (competitorId, stage) {
      const groupedStages = this.getGroupedStages(stage.group, 0)
      const stageNoList = groupedStages.map((stage) => stage.stageNo)
      const competitorGroupedTimes = this.competitionData.time.filter(
        (time) =>
          stageNoList.includes(time.stageNo) &&
          time.competitorId === competitorId &&
          time.time !== null &&
          time.atypicalResultId === null
      )
      // if (competitorGroupedTimes.length !== groupedStages.length) return null
      return competitorGroupedTimes.reduce((acc, time) => acc + time.time, 0)
    },

    getCompetitorMinTimeGroupedStages: function (competitorId, stage) {
      const targetTime = stage.data
      const groupedStages = this.getGroupedStages(stage.group, 2)
      const stageNoList = groupedStages.map((stage) => stage.stageNo)
      const competitorGroupedTimes = this.competitionData.time.filter(
        (time) =>
          stageNoList.includes(time.stageNo) &&
          time.competitorId === competitorId &&
          time.time !== null &&
          time.atypicalResultId === null
      )

      if (competitorGroupedTimes.length !== groupedStages.length) return null

      let sumTime = 0
      for (let time of competitorGroupedTimes) sumTime += time.time
      const avgTime = Math.floor(sumTime / competitorGroupedTimes.length)
      return { time: Math.abs(avgTime - targetTime), realTime: avgTime }
    },

    getCompetitorPenalty: function (competitorId, stage) {
      if (this.afterStage || stage.type === 1) {
        let competitorTimes = null
        const stageNoList = this.getGroupedStages(stage.group, 0).map((stage) => stage.stageNo)
        if (stage.type === 1) {
          competitorTimes = this.competitionData.time.filter(
            (time) => stageNoList.includes(time.stageNo) && time.competitorId === competitorId
          )
        } else {
          competitorTimes = this.competitionData.time.filter(
            (time) =>
              stageNoList.includes(time.stageNo) && time.stageNo <= stage.stageNo && time.competitorId === competitorId
          )
        }
        let penalty = 0
        for (let competitorTime of competitorTimes) {
          if (competitorTime.atypicalResultId) return null
          penalty += competitorTime.penalties.in.reduce((acc, penalty) => acc + penalty.seconds, 0) * 1000
          penalty += competitorTime.penalties.out.reduce((acc, penalty) => acc + penalty.seconds, 0) * 1000
        }
        return penalty
      } else {
        const competitorTime = this.competitionData.time.find(
          (time) => time.stageNo === stage.stageNo && time.competitorId === competitorId
        )
        if (!competitorTime || competitorTime.atypicalResultId) return null

        return competitorTime.penalties.in.reduce((acc, penalty) => acc + penalty.seconds, 0) * 1000
      }
    },
  },
}
</script>

<style scoped lang="scss">
.live {
  font-size: 11px;
  padding: 0px;
  background-color: #00ff00 !important;
  position: absolute;
  width: 100%;
  height: 100%;
}
.fade-enter-active,
.fade-leave-active {
  transition-property: opacity;
  transition-duration: 0.25s;
}

.fade-enter-active {
  transition-delay: 0.25s;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
